import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { SelectionByCategoryEntry } from './types';
import styles from './PunchupSeatsCategoryBreakdown.module.css';

interface PunchupSeatsCategoryBreakdownProps {
	groups: SelectionByCategoryEntry[];
	className?: string;
}

/**
 * PunchupSeatsCategoryBreakdown
 *
 * Renders a list of `(swatch, category label, count of seats)` rows. Used by every
 * seated-event derived display in the green zone (Pool create/edit, Send Tickets seated)
 * to show the user how their chart selection partitions across seats.io categories.
 *
 * Renders nothing when `groups` is empty — the parent typically shows an empty-state hint
 * or error message in that branch. Keeping the empty-render here would force every parent
 * to add a redundant length check.
 */
export const PunchupSeatsCategoryBreakdown: FC<PunchupSeatsCategoryBreakdownProps> = ({ groups, className }) => {
	const { t } = useTranslation();

	if (groups.length === 0) return null;

	return (
		<div className={[styles.breakdown, className].filter(Boolean).join(' ')} data-testid="seats-category-breakdown">
			{groups.map((g) => (
				<div key={g.categoryKey} className={styles.row}>
					<span className={styles.swatch} style={{ backgroundColor: g.color }} />
					<span className={styles.label}>{g.categoryLabel}</span>
					<span className={styles.count}>
						{t('seats-category-breakdown-count', '{{count}} seats', {
							count: g.objectLabels.length,
						})}
					</span>
				</div>
			))}
		</div>
	);
};
